import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { showSnackBar } from "../../../../../../store/common/snackBarSlice";
import BoltButton from "../../../../../../Components/UI/Buttons/BoltButton";
import Confirm from "../../../../../../Components/Popups/Confirm";
import Tooltip from "../../../../../../Components/Tooltip/Tooltip";
import { FiMail, FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";
import {
  getNotificationEmails,
  storeNotificationEmail,
  updateNotificationEmail,
  deleteNotificationEmail,
} from "../../../../../../services/WarehouseServices/notificationEmail.services";

const inputCls =
  "w-full h-9 px-3 border border-ui-border rounded-lg text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand-light transition-all bg-ui-surface";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const firstEmail = (result) => {
  const list = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : result
        ? [result]
        : [];

  return list[0] ?? null;
};

function NotificationEmailManager({ userWarehouseId, className = "" }) {
  const { t } = useTranslation("settings");
  const dispatch = useDispatch();

  // Client specific email
  const [clientEmail, setClientEmail] = useState(null);

  // Warehouse default email
  const [defaultEmail, setDefaultEmail] = useState(null);

  const [newEmail, setNewEmail] = useState("");

  const [adding, setAdding] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const notify = useCallback(
    (text, severity) => dispatch(showSnackBar({ text, severity })),
    [dispatch],
  );

  const getErrorMessage = (res, fallback) =>
    res?.error?.response?.data?.message ?? fallback;

  const loadEmails = useCallback(async () => {
    const res = await getNotificationEmails(userWarehouseId);

    if (res?.error) {
      notify(getErrorMessage(res, t("notificationEmail.loadFailed")), "error");
      return;
    }

    const email = firstEmail(res?.data?.result);

    setClientEmail(null);
    setDefaultEmail(null);

    // Warehouse settings page
    if (!userWarehouseId) {
      if (email) {
        setDefaultEmail(email);
      }
      return;
    }

    // Client settings page
    const isClientEmail =
      Boolean(email?.userWarehouseId) || Boolean(email?.user_warehouse_id);

    if (isClientEmail) {
      setClientEmail(email);
    } else if (email) {
      // Backend returned default warehouse email
      setDefaultEmail(email);
    }
  }, [notify, userWarehouseId, t]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);
  const handleAdd = async () => {
    const value = newEmail.trim();

    if (!isValidEmail(value)) {
      notify(t("notificationEmail.invalid"), "error");
      return;
    }

    setAdding(true);

    const res = await storeNotificationEmail({
      email: value,
      type: userWarehouseId ? "warehouseClient" : "warehouse",
      ...(userWarehouseId ? { user_warehouse_id: userWarehouseId } : {}),
    });

    setAdding(false);

    if (res?.error) {
      notify(getErrorMessage(res, t("notificationEmail.addFailed")), "error");
      return;
    }

    notify(res?.data?.message ?? t("notificationEmail.added"), "success");

    setNewEmail("");
    await loadEmails();
  };

  const startEdit = () => {
    const email = clientEmail || defaultEmail;

    setEditingValue(email?.email ?? "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditingValue("");
  };

  const handleUpdate = async () => {
    const value = editingValue.trim();
    const email = clientEmail || defaultEmail;

    if (!isValidEmail(value)) {
      notify(t("notificationEmail.invalid"), "error");
      return;
    }

    if (value === email.email) {
      cancelEdit();
      return;
    }

    setSavingEdit(true);

    const res = await updateNotificationEmail(email.id, {
      email: value,
    });

    setSavingEdit(false);

    if (res?.error) {
      notify(getErrorMessage(res, t("notificationEmail.updateFailed")), "error");
      return;
    }

    notify(res?.data?.message ?? t("notificationEmail.updated"), "success");

    cancelEdit();
    await loadEmails();
  };

  const handleDelete = async () => {
    const email = clientEmail || defaultEmail;

    if (!email) return;

    setDeleting(true);

    const res = await deleteNotificationEmail(email.id);

    setDeleting(false);
    setConfirmDelete(false);

    if (res?.error) {
      notify(getErrorMessage(res, t("notificationEmail.deleteFailed")), "error");
      return;
    }

    notify(res?.data?.message ?? t("notificationEmail.deleted"), "success");

    if (editing) {
      cancelEdit();
    }

    await loadEmails();
  };

  const renderAddForm = (placeholder) => (
    <>
      <label className="block text-xs font-medium text-ui-text-muted mb-1.5">
        {t("notificationEmail.addLabel")}
      </label>

      <div className="flex items-start gap-2 max-w-md">
        <input
          type="email"
          className={inputCls}
          placeholder={placeholder}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !adding && handleAdd()}
        />

        <BoltButton
          label={t("common:actions.add")}
          variant="primary"
          size="md"
          loading={adding}
          onClick={handleAdd}
        />
      </div>
    </>
  );

  return (
    <div
      className={`bg-ui-surface border border-ui-border rounded-xl p-4 ${className}`}
    >
      <Confirm
        open={confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={handleDelete}
        text={
          userWarehouseId && clientEmail
            ? t("notificationEmail.deleteClientConfirm")
            : t("notificationEmail.deleteDefaultConfirm")
        }
        loader={deleting}
      />

      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg bg-brand-light flex items-center justify-center shrink-0">
          <FiMail size={14} className="text-brand" />
        </div>

        <p className="text-sm font-semibold text-ui-text m-0">
          {t("notificationEmail.title")}
        </p>
      </div>

      <p className="text-xs text-ui-text-muted mb-4">
        {userWarehouseId
          ? t("notificationEmail.clientHint")
          : t("notificationEmail.warehouseHint")}
      </p>
      {/* =========================
          WAREHOUSE SETTINGS PAGE
      ========================== */}
      {!userWarehouseId ? (
        defaultEmail ? (
          editing ? (
            <div className="flex items-center gap-2 max-w-md">
              <input
                type="email"
                className={inputCls}
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !savingEdit && handleUpdate()
                }
              />

              <Tooltip title={t("common:actions.save")}>
                <button
                  type="button"
                  disabled={savingEdit}
                  onClick={handleUpdate}
                  className="text-state-success hover:text-state-success disabled:opacity-50 shrink-0"
                >
                  <FiCheck size={16} />
                </button>
              </Tooltip>

              <Tooltip title={t("common:actions.cancel")}>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="text-ui-text-subtle hover:text-ui-text-muted shrink-0"
                >
                  <FiX size={16} />
                </button>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center gap-2 border border-ui-surface-sunken rounded-lg px-3 py-2 bg-ui-surface-sunken max-w-md">
              <span className="flex-1 text-sm text-ui-text break-all">
                {defaultEmail.email}
              </span>

              <Tooltip title={t("common:actions.edit")}>
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-ui-text-subtle hover:text-brand shrink-0"
                >
                  <FiEdit2 size={15} />
                </button>
              </Tooltip>

              <Tooltip title={t("common:actions.delete")}>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-ui-text-subtle hover:text-state-danger shrink-0"
                >
                  <FiTrash2 size={15} />
                </button>
              </Tooltip>
            </div>
          )
        ) : (
          renderAddForm("warehouse@example.com")
        )
      ) : clientEmail ? (
        /* =========================
            CLIENT EMAIL EXISTS
        ========================== */
        editing ? (
          <div className="flex items-center gap-2 max-w-md">
            <input
              type="email"
              className={inputCls}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && !savingEdit && handleUpdate()
              }
            />

            <Tooltip title={t("common:actions.save")}>
              <button
                type="button"
                disabled={savingEdit}
                onClick={handleUpdate}
                className="text-state-success hover:text-state-success disabled:opacity-50 shrink-0"
              >
                <FiCheck size={16} />
              </button>
            </Tooltip>

            <Tooltip title={t("common:actions.cancel")}>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-ui-text-subtle hover:text-ui-text-muted shrink-0"
              >
                <FiX size={16} />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center gap-2 border border-ui-surface-sunken rounded-lg px-3 py-2 bg-ui-surface-sunken max-w-md">
            <span className="flex-1 text-sm text-ui-text break-all">
              {clientEmail.email}
            </span>

            <Tooltip title={t("common:actions.edit")}>
              <button
                type="button"
                onClick={startEdit}
                className="text-ui-text-subtle hover:text-brand shrink-0"
              >
                <FiEdit2 size={15} />
              </button>
            </Tooltip>

            <Tooltip title={t("common:actions.delete")}>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-ui-text-subtle hover:text-state-danger shrink-0"
              >
                <FiTrash2 size={15} />
              </button>
            </Tooltip>
          </div>
        )
      ) : (
        /* =========================
            USING DEFAULT EMAIL
        ========================== */
        <>
          {defaultEmail && (
            <>
              <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-brand-light border border-brand-light max-w-md">
                <FiMail size={14} className="text-brand mt-0.5 shrink-0" />

                <p className="text-xs text-brand-hover m-0">
                  <Trans
                    t={t}
                    i18nKey="notificationEmail.usingDefault"
                    values={{ email: defaultEmail.email }}
                    components={{
                      email: <span className="font-semibold break-all ml-1" />,
                    }}
                  />
                </p>
              </div>

              <div className="flex items-center gap-2 border border-ui-surface-sunken rounded-lg px-3 py-2 bg-ui-surface-sunken max-w-md mb-4">
                <span className="flex-1 text-sm text-ui-text break-all">
                  {defaultEmail.email}
                </span>
              </div>
            </>
          )}

          {renderAddForm("client@example.com")}
        </>
      )}
    </div>
  );
}

export default NotificationEmailManager;
